class MemoryStorage implements Storage {
  private store = new Map<string, string>();

  get length(): number {
    return this.store.size;
  }

  clear(): void {
    for (const key of this.store.keys()) {
      Reflect.deleteProperty(this, key);
    }
    this.store.clear();
  }

  getItem(key: string): string | null {
    const normalizedKey = String(key);
    return this.store.has(normalizedKey)
      ? (this.store.get(normalizedKey) ?? null)
      : null;
  }

  key(index: number): string | null {
    if (index < 0 || index >= this.store.size) {
      return null;
    }
    return Array.from(this.store.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    const normalizedKey = String(key);
    this.store.delete(normalizedKey);
    Reflect.deleteProperty(this, normalizedKey);
  }

  setItem(key: string, value: string): void {
    const normalizedKey = String(key);
    const normalizedValue = String(value);
    this.store.set(normalizedKey, normalizedValue);
    Reflect.set(this, normalizedKey, normalizedValue);
  }
}

const memoryStorage = new MemoryStorage();
let accessCheckFailed = false;

function getNativeStorage(): Storage | null {
  if (accessCheckFailed || typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage ?? null;
  } catch {
    accessCheckFailed = true;
    return null;
  }
}

function tryWithNative<T>(fn: (storage: Storage) => T): {
  ok: boolean;
  value?: T;
} {
  const storage = getNativeStorage();
  if (!storage) {
    return { ok: false };
  }

  try {
    return { ok: true, value: fn(storage) };
  } catch {
    return { ok: false };
  }
}

class SafeLocalStorage implements Storage {
  get length(): number {
    const result = tryWithNative((storage) => storage.length);
    return result.ok ? (result.value as number) : memoryStorage.length;
  }

  clear(): void {
    const result = tryWithNative((storage) => storage.clear());
    memoryStorage.clear();
    if (!result.ok) {
      return;
    }
  }

  getItem(key: string): string | null {
    const result = tryWithNative((storage) => storage.getItem(key));
    if (result.ok) {
      return (result.value as string | null) ?? null;
    }
    return memoryStorage.getItem(key);
  }

  key(index: number): string | null {
    const result = tryWithNative((storage) => storage.key(index));
    if (result.ok) {
      return (result.value as string | null) ?? null;
    }
    return memoryStorage.key(index);
  }

  removeItem(key: string): void {
    const result = tryWithNative((storage) => storage.removeItem(key));
    memoryStorage.removeItem(key);
    if (!result.ok) {
      return;
    }
  }

  setItem(key: string, value: string): void {
    const result = tryWithNative((storage) => storage.setItem(key, value));
    memoryStorage.setItem(key, value);
    if (!result.ok) {
      return;
    }
  }
}

export const safeLocalStorage: Storage = new SafeLocalStorage();

export function ensureSafeLocalStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const nativeStorage = getNativeStorage();
  if (nativeStorage) {
    try {
      nativeStorage.getItem('__storage_test__');
      return;
    } catch {
      // Fall through to patch with in-memory storage.
    }
  }

  try {
    Object.defineProperty(window, 'localStorage', {
      value: memoryStorage,
      configurable: true,
      enumerable: true,
    });
  } catch {
    try {
      (window as { localStorage?: Storage }).localStorage = memoryStorage;
    } catch {
      // Ignore if we cannot patch the global.
    }
  }
}

export function getStorageKeys(storage: Storage = safeLocalStorage): string[] {
  const keys: string[] = [];
  const length = storage.length;
  for (let i = 0; i < length; i += 1) {
    const key = storage.key(i);
    if (key != null) {
      keys.push(key);
    }
  }
  return keys;
}
